import { useId } from 'react';
import { makeStyles, shorthands, tokens, Text } from '@fluentui/react-components';

/**
 * Radar chart of posture across the Zero Trust pillars.
 *
 * Hand drawn SVG rather than a charting library: this is six points on a
 * hexagon, and pulling in a charting dependency for it would add more to the
 * bundle than the entire application code.
 *
 * Two deliberate differences from the commercial tools this resembles:
 *
 * There is no industry average line. We have no industry data, and a made up
 * comparison is worse than none. The optional second series is the customer's
 * own previous collection, which is real.
 *
 * A pillar with no findings scores 100 and is labelled as such elsewhere, but
 * the caption states plainly that this reflects what was found rather than
 * proven compliance.
 */

export interface RadarSeries {
  label: string;
  colour: string;
  /** One value per axis, 0 to 100. */
  values: number[];
  dashed?: boolean;
}

export interface RadarChartProps {
  axes: string[];
  series: RadarSeries[];
  size?: number;
  caption?: string;
}

export function RadarChart({ axes, series, size = 320, caption }: RadarChartProps) {
  const styles = useStyles();
  const titleId = useId();

  // Labels sit outside the outer ring, and the longest of them ("Infrastructure")
  // needs roughly 90px. Reserving it here rather than clipping, which is what
  // the first version did.
  const labelRoom = 92;
  const cx = size / 2;
  const cy = size / 2;
  const radius = Math.max(40, size / 2 - labelRoom);
  const rings = [25, 50, 75, 100];

  // Start at twelve o'clock and work clockwise, which is how people read a
  // dial. The default of starting at three o'clock reads oddly.
  const angleFor = (i: number) => (Math.PI * 2 * i) / axes.length - Math.PI / 2;

  const pointAt = (i: number, value: number) => {
    const a = angleFor(i);
    const r = (Math.max(0, Math.min(100, value)) / 100) * radius;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
  };

  const polygon = (values: number[]) =>
    values.map((v, i) => pointAt(i, v).join(',')).join(' ');

  return (
    <div className={styles.wrap}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>
          Posture by Zero Trust pillar: {axes.map((a, i) => `${a} ${series[0]?.values[i] ?? 0}`).join(', ')}
        </title>

        {rings.map((r) => (
          <polygon
            key={r}
            points={polygon(axes.map(() => r))}
            fill="none"
            stroke={tokens.colorNeutralStroke2}
            strokeWidth={1}
          />
        ))}

        {axes.map((_, i) => {
          const [x, y] = pointAt(i, 100);
          return (
            <line key={i} x1={cx} y1={cy} x2={x} y2={y}
                  stroke={tokens.colorNeutralStroke2} strokeWidth={1} />
          );
        })}

        {series.map((s) => (
          <g key={s.label}>
            <polygon
              points={polygon(s.values)}
              fill={s.colour}
              fillOpacity={s.dashed ? 0.06 : 0.18}
              stroke={s.colour}
              strokeWidth={2}
              strokeDasharray={s.dashed ? '5 4' : undefined}
              strokeLinejoin="round"
            />
            {!s.dashed && s.values.map((v, i) => {
              const [x, y] = pointAt(i, v);
              return <circle key={i} cx={x} cy={y} r={3.5} fill={s.colour} />;
            })}
          </g>
        ))}

        {axes.map((label, i) => {
          const [x, y] = pointAt(i, 118);
          const centred = Math.abs(x - cx) < 8;
          // Nudge away from the spoke so the text never overlaps it.
          const nudge = centred ? 0 : x > cx ? 6 : -6;
          return (
            <text
              key={label}
              x={x + nudge}
              y={y}
              textAnchor={centred ? 'middle' : x > cx ? 'start' : 'end'}
              dominantBaseline="middle"
              fontSize={11}
              fontWeight={600}
              fill={tokens.colorNeutralForeground2}
            >
              {label}
            </text>
          );
        })}
      </svg>

      <div className={styles.legend}>
        {series.map((s) => (
          <span key={s.label} className={styles.legendItem}>
            <span className={styles.swatch}
                  style={{ backgroundColor: s.dashed ? 'transparent' : s.colour,
                           borderColor: s.colour }} />
            <Text size={200}>{s.label}</Text>
          </span>
        ))}
      </div>

      {caption && <Text className={styles.caption}>{caption}</Text>}
    </div>
  );
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: tokens.spacingVerticalS },
  legend: { display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalM, justifyContent: 'center' },
  legendItem: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalXS },
  swatch: {
    width: '12px', height: '12px',
    ...shorthands.borderRadius('3px'),
    ...shorthands.border('2px', 'solid', tokens.colorNeutralStroke1),
  },
  caption: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
    maxWidth: '360px',
    lineHeight: tokens.lineHeightBase200,
  },
});
